import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';

import styles from './PrimaryInputField.module.css';

/**
 * Перенос `lib/src/presentation/widgets/primary_input_field.dart`.
 *
 * Во Flutter полем управлял `TextEditingController`: страница читала из него
 * текст и чистила его после отправки. В React эту роль играет [useTextController]
 * ниже — страница держит значение у себя и передаёт его в `value`/`onChange`,
 * так что код страницы читается так же, как дартовый.
 */

/** Замена `TextEditingController`. */
export interface TextController<E extends HTMLElement = HTMLInputElement> {
  /** `controller.text` */
  readonly value: string;
  /** `controller.text = next` */
  readonly setValue: (next: string) => void;
  /** `controller.clear()` */
  readonly clear: () => void;
  /** Доступ к самому полю — `focusNode` для фокуса и выделения. */
  readonly ref: RefObject<E | null>;
}

export function useTextController<E extends HTMLElement = HTMLInputElement>(
  initialValue = '',
): TextController<E> {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<E | null>(null);
  const clear = useCallback(() => setValue(''), []);
  // Новый объект на каждое изменение — состояние не правится на месте.
  return useMemo(() => ({ value, setValue, clear, ref }), [value, clear]);
}

/**
 * `onTapOutside: (event) => _focusNode.unfocus()`.
 *
 * Во Flutter это `TapRegion` вокруг поля; в вебе — слушатель `pointerdown`
 * на документе в фазе перехвата, чтобы снять фокус раньше, чем нажатие
 * обработает кто-то ещё.
 */
function useUnfocusOnTapOutside(
  fieldRef: RefObject<HTMLElement | null>,
  inputRef: RefObject<HTMLInputElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    const onPointerDown = (event: PointerEvent): void => {
      const field = fieldRef.current;
      const target = event.target;
      if (!field || !(target instanceof Node) || field.contains(target)) return;
      inputRef.current?.blur();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [enabled, fieldRef, inputRef]);
}

export interface PrimaryInputFieldProps {
  /** Подпись над полем, `AppStyles.s16w600`. */
  title?: string;
  /** `InputDecoration.hintText` */
  hintText?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  onFieldSubmitted?: (value: string) => void;
  onFocusChanged?: (hasFocus: boolean, value: string) => void;
  unfocusOnTapOutside?: boolean;
  /** `autofocus:` из Dart. */
  autoFocus?: boolean;
  /** `keyboardType: TextInputType.url` → `inputMode="url"`. */
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  /** `textInputAction: TextInputAction.go` → `enterKeyHint="go"`. */
  enterKeyHint?: InputHTMLAttributes<HTMLInputElement>['enterKeyHint'];
  /** Для адресов и доменов выключать: клавиатура иначе «исправляет» их. */
  autoCorrect?: boolean;
  /** `enableSuggestions` из Dart. */
  spellCheck?: boolean;
  /** Пароль планшета — точки вместо символов. */
  obscureText?: boolean;
  /** `autocomplete` поля: для пароля киоска — `new-password`, без подстановки. */
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  className?: string;
}

export function PrimaryInputField(props: PrimaryInputFieldProps): ReactElement {
  const {
    title, hintText, value, defaultValue, onChange, inputRef, onFieldSubmitted,
    onFocusChanged, unfocusOnTapOutside = true, autoFocus = false, inputMode,
    enterKeyHint, autoCorrect = true, spellCheck = true, obscureText = false,
    autoComplete, prefixIcon, suffixIcon, className,
  } = props;

  const fieldRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLInputElement | null>(null);
  // Единственная разрешённая мутация — `ref.current`: так React устроен.
  const attachInput = useCallback(
    (node: HTMLInputElement | null): void => {
      innerRef.current = node;
      if (inputRef) inputRef.current = node;
    },
    [inputRef],
  );
  useUnfocusOnTapOutside(fieldRef, innerRef, unfocusOnTapOutside);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    onFieldSubmitted?.(event.currentTarget.value);
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void =>
    onChange?.(event.target.value);
  const handleFocus = (hasFocus: boolean) => (event: FocusEvent<HTMLInputElement>): void =>
    onFocusChanged?.(hasFocus, event.target.value);

  // Контролируемое поле, если значение пришло снаружи; иначе — свободное.
  const valueProps = value === undefined ? { defaultValue } : { value };

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      {title !== undefined && <span className={styles.title}>{title}</span>}
      <div className={styles.field} ref={fieldRef}>
        {prefixIcon}
        <input
          {...valueProps}
          ref={attachInput}
          className={styles.input}
          type={obscureText ? 'password' : 'text'}
          placeholder={hintText}
          autoFocus={autoFocus}
          inputMode={inputMode}
          enterKeyHint={enterKeyHint}
          autoCorrect={autoCorrect ? 'on' : 'off'}
          autoCapitalize={autoCorrect ? 'sentences' : 'off'}
          spellCheck={spellCheck}
          autoComplete={autoComplete}
          onChange={handleChange}
          onFocus={handleFocus(true)}
          onBlur={handleFocus(false)}
          onKeyDown={handleKeyDown}
        />
        {suffixIcon}
      </div>
    </div>
  );
}
